import React from 'react';
import { Link } from 'react-router-dom';
import { animated, useTransition } from '@react-spring/web';

import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons/faArrowLeft';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck';
import { faFilmSimple } from '@fortawesome/pro-solid-svg-icons/faFilmSimple';
import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons/faPaperPlane';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAnimatedRouteState } from '@wildidea/components/AnimatedRoutes/AnimatedRoutes';
import { LinkButton } from '@wildidea/components/Button/LinkButton';
import { SubmitButton } from '@wildidea/components/Button/SubmitButton';
import { Card } from '@wildidea/components/Card/Card';
import { ControlsRow } from '@wildidea/components/ControlsRow/ControlsRow';
import { Fieldset } from '@wildidea/components/Fieldset/Fieldset';
import { FormField } from '@wildidea/components/FormField/FormField';
import { useFormSubmitHandler } from '@wildidea/components/hooks/useFormSubmitHandler';
import { isSuccessState, usePromiseState } from '@wildidea/components/hooks/usePromiseState';
import { MenuCheckbox } from '@wildidea/components/Menu/Menu';
import { MenuButton } from '@wildidea/components/Menu/MenuButton';
import { StatusPill } from '@wildidea/components/Pill/StatusPill';
import { RadioButton } from '@wildidea/components/Radio/RadioButton';
import { useThemeAnimations } from '@wildidea/components/Theme/Theme';
import { useToaster } from '@wildidea/components/Toaster/Toaster';
import { TooltipHost } from '@wildidea/components/Tooltip/Tooltip';

import { submitTeamInquiry } from '@data/teamInquiry.js';
import { track } from '@data/track.js';

import { recordWildideaEvent } from '@app/telemetry.js';

import logo from '../home/img/logo.png';

import missionControl from './img/mission-control.webp';

import * as css from './TeamsRoute.module.css';

/**
 * Everything in this list is shipped and usable by a team today — see
 * rescueally-cloud/docs/TEAM-ACCOUNTS.md and the app guide's
 * delta/guide/features/TeamAccounts.md. Nothing from the Mission Control
 * vision belongs here; that lives in WHATS_NEXT below, clearly labeled.
 */
const SHIPPED_TODAY = [
  'Every rescue your team documents lands in one shared history. Viewers and admins see the team’s records; members keep their own.',
  'Three roles that match how teams actually run: member, viewer, admin.',
  'Invite by email. A seat activates when that person signs in — no license keys to hand out.',
  'Your team’s open rescues show up while they are still running, not after the fact.',
  'Export the record when you need it out: CSV, spreadsheet, or NEMSIS XML.',
  'Records stay with the team when a responder moves on.',
  'Your team’s data is walled off from every other team, and we test that continuously.'
];

const WHATS_NEXT = ['Callout and dispatch', 'Live field picture', 'Triage board', 'EMS handoff'];

const TEAM_TYPES = [
  'Search & Rescue',
  'EMS / Fire / Rescue',
  'Ski Patrol / Mountain Operations',
  'Guiding / Outfitting / Expeditions',
  'Outdoor Recreation / Education',
  'Community / Outdoor Access Organizations',
  'Parks / Land Management / Field Programs',
  'Wilderness Medicine / Medical Training',
  'Disaster Response / Humanitarian Health',
  'Global Health / Community Health',
  'Event / Mass Gathering Medical',
  'Remote Worksite / Occupational Health',
  'Other'
];

const TEAM_SIZES = ['1–10', '11–25', '26–50', '50+'];

/** All optional — interest signals, never a barrier to submitting. */
const INTERESTS = [
  'Field Patient Assessment & Documentation',
  'Medical Reference & Decision Support',
  'Patient Handoff & Information Sharing',
  'Training & Scenarios',
  'Team Licenses / Organization-Wide Access',
  'Team Reporting & Administration',
  'Future Mission Coordination Tools',
  'Something Else',
  'Not Sure Yet'
];

const STAGES = [
  'Just Exploring',
  'Interested in a Demo',
  'Interested in a Pilot',
  'Planning to Implement',
  'Not Sure Yet'
];

const TeamsRoute: React.FC<{}> = () => {
  const { phase, onTransitionEnd } = useAnimatedRouteState();
  const active = phase === 'enter' || phase === 'active';
  const { fromBelow } = useThemeAnimations('Common');
  const contentTransition = useTransition(active, { ...fromBelow, onRest: onTransitionEnd });
  return contentTransition((style, active) =>
    active ? (
      <animated.div style={style} className={css.layout}>
        {/* The route layout is position:fixed at z-index 1000, so anything
            portaled to document.body (Menu/Tooltip, z-index 100) paints
            BEHIND it. Hosting tooltips inside the layer instead. */}
        <TooltipHost>
          <div className={css.pageHeader}>
            <Link to="/" aria-label="Back">
              <FontAwesomeIcon icon={faArrowLeft} />
            </Link>
            <img src={logo} className={css.headerLogo} alt="" />
            <h1>Mission Control</h1>
          </div>

          <section className={css.hero}>
            <div className={css.heroInner}>
              <div className={css.heroCopy}>
                <div className={css.eyebrow}>
                  For SAR teams, guide services, and course providers
                </div>
                <h2>Your team saves lives. We've got your back.</h2>
                <p className={css.heroSub}>
                  Rescue Ally Mission Control is coming soon, providing situational awareness and
                  unified documentation for rescue and field teams. We're building it today, for
                  teams like yours.
                </p>
                <div className={css.heroLinks}>
                  <LinkButton
                    appearance="fill"
                    color="accent"
                    href="/demo/team"
                    icon={<FontAwesomeIcon icon={faFilmSimple} />}
                  >
                    Watch the demo
                  </LinkButton>
                </div>
              </div>
              <figure className={css.heroShot}>
                <img
                  src={missionControl}
                  alt="The Mission Control concept dashboard: incident intake for two overdue hikers — reporting party details, subject descriptions, last known point, conditions, the on-call roster, and a callout ready to page out."
                />
                <figcaption>Mission Control — concept</figcaption>
              </figure>
            </div>
          </section>

          <section className={css.inquire}>
            <InquiryCard />
          </section>

          <section className={css.section}>
            <h2>What your team gets today</h2>
            <ul className={css.featureList}>
              {SHIPPED_TODAY.map((feature) => (
                <li key={feature}>
                  <FontAwesomeIcon icon={faCheck} className={css.checkIcon} />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className={css.section}>
            <h2>What we are building next</h2>
            <p>
              The demo imagines a new operations layer over the assessment records the app already
              writes: the callout, the shared picture of who is where, the handoff to EMS.
            </p>
            <div className={css.chips}>
              {WHATS_NEXT.map((item) => (
                <span key={item} className={css.chip}>
                  {item}
                </span>
              ))}
            </div>
          </section>

          <footer className={css.footer}>
            <p>
              Not a team? Rescue Ally is a one-time purchase for individuals — see{' '}
              <Link to="/pricing">Pricing</Link>. Instructors and course leaders may want the{' '}
              <Link to="/ambassadors">Ambassador program</Link> instead.
            </p>
          </footer>
        </TooltipHost>
      </animated.div>
    ) : null
  );
};

const InquiryCard: React.FC<{}> = () => {
  const addToast = useToaster();
  const [submitState, setSubmitPromise] = usePromiseState<void>();

  // Funnel instrumentation. The email beacon fires the moment a plausible
  // address is typed — if the form is then abandoned, the ra-mkt log line is
  // how we find out who it was and follow up.
  const [interests, setInterests] = React.useState<string[]>([]);
  const toggleInterest = (id: string) =>
    setInterests((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));

  const startedRef = React.useRef(false);
  const submittedRef = React.useRef(false);
  const sentEmailRef = React.useRef('');
  const emailTimerRef = React.useRef<number>();
  const onFormFocus = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    track({ event: 'teams-form-start' });
  };
  const captureEmail = (value: string, immediate: boolean) => {
    window.clearTimeout(emailTimerRef.current);
    const send = () => {
      const email = value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) return;
      if (email === sentEmailRef.current) return;
      sentEmailRef.current = email;
      track({ event: 'teams-email', detail: { em: email } });
    };
    if (immediate) send();
    else emailTimerRef.current = window.setTimeout(send, 800);
  };
  React.useEffect(function trackAbandonment() {
    const onPageHide = () => {
      if (startedRef.current && !submittedRef.current) {
        track({ event: 'teams-abandon', detail: { em: sentEmailRef.current || undefined } });
      }
    };
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      onPageHide();
    };
  }, []);

  const { onSubmit, promise } = useFormSubmitHandler(async (formEl) => {
    const data = new FormData(formEl);
    // Honeypot: a real person never fills a field they cannot see. Resolve as
    // if it worked so a bot gets no signal about why nothing happened.
    if (data.get('website')) {
      const ignored = Promise.resolve();
      setSubmitPromise(ignored);
      return ignored;
    }

    const inquiry = {
      teamName: String(data.get('teamName') || '').trim(),
      contactName: String(data.get('contactName') || '').trim(),
      contactRole: String(data.get('contactRole') || '').trim(),
      email: String(data.get('email') || '').trim(),
      areaOfOperation: String(data.get('areaOfOperation') || '').trim(),
      teamType: String(data.get('teamType') || ''),
      teamSize: String(data.get('teamSize') || ''),
      notes: String(data.get('notes') || '').trim() || undefined,
      interests: interests.join(', ') || undefined,
      stage: String(data.get('stage') || '') || undefined,
      utmSource: window.sessionStorage.getItem('utm/source') || undefined,
      referrer: document.referrer || undefined
    };

    const submission = submitTeamInquiry(inquiry).then(
      () => {
        submittedRef.current = true;
        track({
          event: 'teams-submit',
          detail: { type: inquiry.teamType, size: inquiry.teamSize }
        });
        recordWildideaEvent({
          type: 'rescueally.teamInquiry',
          detail: { teamType: inquiry.teamType, teamSize: inquiry.teamSize }
        });
      },
      (error: unknown) => {
        track({ event: 'teams-submit-error' });
        addToast({
          header: 'That did not send',
          body: 'Something went wrong on our end. Please email support@rescueally.org and we will pick it up from there.',
          type: 'error',
          timeout: false
        });
        throw error;
      }
    );
    setSubmitPromise(submission);
    return submission;
  });

  if (isSuccessState(submitState)) {
    return (
      <Card color="paper1" className={css.formCard}>
        <div className={css.sentIcon}>
          <FontAwesomeIcon icon={faCircleCheck} />
        </div>
        <h2>Got it.</h2>
        <p>
          Your inquiry is with us. Expect a reply from a person within a couple of days — usually
          sooner, and usually with questions about how your team runs.
        </p>
        <p className={css.formNote}>
          In a hurry, or want to add something? Write to{' '}
          <a href="mailto:support@rescueally.org">support@rescueally.org</a>.
        </p>
      </Card>
    );
  }

  return (
    <Card color="paper1" className={css.formCard}>
      <h2>Tell us about your team</h2>
      <form onSubmit={onSubmit} noValidate className={css.form} onFocus={onFormFocus}>
        <FormField label="Team or organization name">
          <input name="teamName" type="text" autoComplete="organization" required maxLength={120} />
        </FormField>

        <div className={css.formRow}>
          <FormField label="Your name">
            <input name="contactName" type="text" autoComplete="name" required maxLength={120} />
          </FormField>
          <FormField label="Your role">
            <input
              name="contactRole"
              type="text"
              autoComplete="organization-title"
              required
              maxLength={120}
              placeholder="Team lead, training officer, medical director…"
            />
          </FormField>
        </div>

        <FormField label="Email">
          <input
            name="email"
            type="email"
            autoComplete="email"
            required
            maxLength={254}
            onChange={(e) => captureEmail(e.currentTarget.value, false)}
            onBlur={(e) => captureEmail(e.currentTarget.value, true)}
          />
        </FormField>

        <FormField
          label="Where you operate"
          helpMessage="County and state, or province and country."
        >
          <input name="areaOfOperation" type="text" required maxLength={120} />
        </FormField>

        <FormField label="What kind of team">
          <select name="teamType" required defaultValue="">
            <option value="" disabled>
              Choose the closest fit…
            </option>
            {TEAM_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </FormField>

        <Fieldset legend="Roughly how many people" appearance="legend" className={css.fieldset}>
          <ControlsRow justify="start" className={css.chipRow}>
            {TEAM_SIZES.map((size) => (
              <RadioButton
                key={size}
                name="teamSize"
                value={size}
                required
                button={{ color: 'paper2', checkedColor: 'accent', elevated: true }}
              >
                {size}
              </RadioButton>
            ))}
          </ControlsRow>
        </Fieldset>

        <Fieldset
          legend="What are you interested in using Rescue Ally for? (optional)"
          appearance="legend"
          className={css.fieldset}
        >
          <ControlsRow justify="start" className={css.chipRow}>
            <MenuButton
              button={{ color: 'paper2', elevated: true }}
              menu={{ color: 'paper2' }}
              label={interests.length ? `${interests.length} selected` : 'Choose any that apply…'}
            >
              {INTERESTS.map((interest) => (
                <MenuCheckbox
                  key={interest}
                  id={interest}
                  checked={interests.includes(interest)}
                  onMenuSelect={toggleInterest}
                >
                  {interest}
                </MenuCheckbox>
              ))}
            </MenuButton>
            {interests.map((interest) => (
              <StatusPill key={interest} color="info">
                {interest}
              </StatusPill>
            ))}
          </ControlsRow>
        </Fieldset>

        <FormField
          label="What would you like Rescue Ally to help your team do better? (optional)"
          helpMessage="Tell us about your current workflow, a challenge you're trying to solve, or what caught your attention."
        >
          <textarea name="notes" rows={3} maxLength={2000} />
        </FormField>

        <Fieldset
          legend="Where are you in the process? (optional)"
          appearance="legend"
          className={css.fieldset}
        >
          <ControlsRow justify="start" className={css.chipRow}>
            {STAGES.map((stage) => (
              <RadioButton
                key={stage}
                name="stage"
                value={stage}
                button={{ color: 'paper2', checkedColor: 'accent', elevated: true }}
              >
                {stage}
              </RadioButton>
            ))}
          </ControlsRow>
        </Fieldset>

        {/* HP */}
        <div className={css.website} aria-hidden="true">
          <label>
            Website
            <input name="website" type="text" tabIndex={-1} autoComplete="off" />
          </label>
        </div>

        <SubmitButton
          appearance="fill"
          color="info"
          promise={promise}
          icon={<FontAwesomeIcon icon={faPaperPlane} />}
          className={css.submit}
        >
          Talk With Us
        </SubmitButton>
      </form>
    </Card>
  );
};

export default TeamsRoute;
